import { TranslateExcelBusiness, translateExcelBusinessProperties } from '@/coreDomainsModule/translateExcel';
import _ from 'lodash';
import { simpleCopy } from '@/utils/deepCopy';
import { ProgressUpdateFunction } from '../../../coreDomainsModule/translateExcel';
import { makeAggregateGroup } from '@/appLogic/business/grouping';
import { getTranslateBusinessIdManager } from '@/utils/translateBusinessIdManager';
import { InventoryService } from '@/coreDomainsModule/services/inventoryService';
import { IBusiness, extendedBusinessToIBusiness } from '@/coreDomainsModule/entities/businessBase';
import { RegisterBusinessRepository } from '@/coreInfrastructureModule/Repositories/RegisterBusinessRepository';
import { UpdateRegisterBusinessRepository } from '@/coreInfrastructureModule/Repositories/UpdateRegisterBusinessRepository';
import { PutRegisterBusinessUseCase } from '@/registerInputModule/useCases/PutRegisterBusinessUseCase';
import { UpdateRegisterBusinessUseCase } from '@/registerInputModule/useCases/UpdateRegisterBusinessUseCase';
import { INetworkResult } from '@/coreDomainsModule/networkResult';
import { IError } from '@/coreInfrastructureModule/interfaces/IError';
import { RegisterBusinessForTranslateExcelBusinessConditionalType } from '@/registerInputModule/pages/interfaces/translateExcelStore';
import { i18n } from '@/i18n';

const t = i18n.t;

/**
 * 集計行以外の取引情報の登録を行う
 * businessIdはシステム登録処理後に付与されるので、一旦集計行以外を登録するようにしている(関連取引のグループ化に必要)
 * @param businesses TranslateBusinessContentのレコード
 * @returns businessId付与済みの関連取引情報
 */
export const registerNotAggregateBusiness = async (
  businesses: Record<string, TranslateExcelBusiness>
): Promise<TranslateExcelBusiness[]> => {
  // 集計行以外の取引情報
  const relatedTranslateExcelBusinessArray: TranslateExcelBusiness[] =
    Object.values(businesses).filter((obj) => !obj.aggregateBusiness) ?? [];
  // IBusinessにアップキャスト（現状する必要ないので閉じてる）
  // const relatedBusinessArray: IBusiness[] = simpleCopy(
  //   extendedBusinessToIBusiness(relatedTranslateExcelBusinessArray, translateExcelBusinessProperties)
  // );
  return relatedTranslateExcelBusinessArray;
};

/**
 * 集計行の取引情報をシステムに登録する
 * 関連取引の登録(グループ化)、関連取引の内示、販売実績数量の集計(集計行に登録する数量の計算)を行ってから登録を行うようにしている
 * @param relatedBusinessArray 関連取引情報(businessId付与済み)
 * @param businesses TranslateBusinessContentのレコード
 * @param executionDate 在庫の出力日時(LEANPATHへの登録日ではなく、ユーザー設定項目のやつ)
 * @param useUpsert 既存データの更新を行うかどうか
 * @param isUpdate 更新モードかどうか
 */
export const registerAggregateBusiness = async <T extends boolean>(
  relatedBusinessArray: TranslateExcelBusiness[],
  businesses: Record<string, TranslateExcelBusiness>,
  executionDate: Date,
  useUpsert: boolean,
  isUpdate: T
): Promise<INetworkResult<RegisterBusinessForTranslateExcelBusinessConditionalType<T>, IError>> => {
  if (isUpdate) {
    const updateRegisterBusinessRepository = new UpdateRegisterBusinessRepository();
    const updateRegisterBusinessUseCase = new UpdateRegisterBusinessUseCase();
    const translateExcelBusinessArray: TranslateExcelBusiness[] = Object.values(businesses) ?? [];
    // 在庫情報の合算処理を行う(同一年月の在庫数量を合算する)
    translateExcelBusinessArray.forEach((translateBusiness) => {
      translateBusiness.mvpInventory = InventoryService.sumInventoryAmountInYearMonth(translateBusiness.mvpInventory);
    });

    const updateResult = await updateRegisterBusinessUseCase.Execute(
      updateRegisterBusinessRepository,
      translateExcelBusinessArray,
      executionDate
    );
    return updateResult as INetworkResult<RegisterBusinessForTranslateExcelBusinessConditionalType<T>, IError>;
  }

  // 集計行の取引情報
  const aggregateTranslateExcelBusinessArray: TranslateExcelBusiness[] =
    Object.values(businesses).filter((obj) => obj.aggregateBusiness) ?? [];
  // filteredAggregateTranslateExcelBusinessにexecutionDateを付与する
  aggregateTranslateExcelBusinessArray.forEach((obj) => {
    obj.executionDate = executionDate;
  });
  // グループ化を行なった取引情報を入れるための配列
  const createdAggregateGroupTranslateExcelBusinessArray: TranslateExcelBusiness[] = [];
  // 関連取引のグループ化
  for (const aggregateTranslateExcelBusiness of aggregateTranslateExcelBusinessArray) {
    // 関連取引をIBusinessの配列としてまとめる
    const registeredBusinessArray = await getTranslateBusinessIdManager().makeAggregateLineRelatedBusiness(
      relatedBusinessArray,
      aggregateTranslateExcelBusiness
    );

    // 集計行の取引情報のうち、在庫情報の合算処理を行う(同一年月の在庫数量を合算する)
    aggregateTranslateExcelBusiness.mvpInventory = InventoryService.sumInventoryAmountInYearMonth(
      aggregateTranslateExcelBusiness.mvpInventory
    );

    // 集計行の取引情報に関連取引のグループ化と関連取引に登録されている数量を集計して集計行の取引情報に登録する
    createdAggregateGroupTranslateExcelBusinessArray.push(
      simpleCopy(await makeAggregateGroup(registeredBusinessArray, aggregateTranslateExcelBusiness))
    );
  }
  const createdGroupAggregateBusinessArray: IBusiness[] = extendedBusinessToIBusiness(
    createdAggregateGroupTranslateExcelBusinessArray,
    translateExcelBusinessProperties
  );

  const registerBusinessRepository = new RegisterBusinessRepository();
  const putRegisterBusinessUseCase = new PutRegisterBusinessUseCase();

  const putResult = await putRegisterBusinessUseCase.Execute(
    registerBusinessRepository,
    relatedBusinessArray,
    createdGroupAggregateBusinessArray,
    executionDate,
    useUpsert
  );
  return putResult as INetworkResult<RegisterBusinessForTranslateExcelBusinessConditionalType<T>, IError>;
};

/**
 * エクセル転記の結果をLEANPATHのシステムに取り込む
 * @param businesses
 * @param executionDate 実行日
 * @param successTranslateKeys 転記が成功したエクセルデータのキー
 * @param useUpsert 既存データの更新を行うかどうか
 * @param isUpdate 更新モードかどうか
 * @param updateProgressFunc
 * @memberof FileManager
 */
export const registerBusinessForTranslateExcelBusiness = async <T extends boolean>(
  businesses: Record<string, TranslateExcelBusiness>,
  executionDate: Date,
  successTranslateKeys: Record<string, boolean>,
  useUpsert: boolean,
  isUpdate: T,
  updateProgressFunc: ProgressUpdateFunction
): Promise<INetworkResult<RegisterBusinessForTranslateExcelBusinessConditionalType<T>, IError>> => {
  // 集計行以外の取引情報 → 集計行の取引情報の順番にシステムへ取り込む
  // 集計行に関連取引のbusinessId群をグループ要素として持たせる
  // businessIdは、bulkRegisterなどのシステムへの登録処理の最初に生成されるため、集計行以外の取引情報を先に登録処理にかけることでbusinessIdの付与を行う

  // システムに登録されている取引情報を初期化する
  await updateProgressFunc(t('システムへの登録準備中・・・'), 0);

  // 集計行以外(関連取引情報)をシステムに登録する
  await updateProgressFunc(t('システムへ情報を登録中・・・'), 15);

  // 転記が成功したデータのみを取り込む(更新モードの場合は、すべてのデータを取り込む)
  for (const key in businesses) {
    if (successTranslateKeys[key] == null && !isUpdate) {
      delete businesses[key];
    }
  }
  const registeredNotAggregateBusiness = await registerNotAggregateBusiness(businesses);

  // 集計行をシステムに登録する
  await updateProgressFunc(t('システムへ情報を登録中・・・'), 15);

  const registerResult = await registerAggregateBusiness(registeredNotAggregateBusiness, businesses, executionDate, useUpsert, isUpdate);
  // ゲージがMAXまで行ったのを確認できずに閉じるのが気持ち悪いので小細工
  await new Promise((resolve: any) => {
    setTimeout(() => {
      resolve();
    }, 500);
  });

  return registerResult as INetworkResult<RegisterBusinessForTranslateExcelBusinessConditionalType<T>, IError>;
};
